import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { teleconsultUrl, BRAND } from "@/lib/constants";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/health-notes", label: "Health Notes" },
  { to: "/research", label: "Research Hub" },
  { to: "/b2b", label: "B2B Partnerships" },
  { to: "/contact", label: "Contact" },
];

const testIdFor = (label) =>
  `nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-brand"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
        >
          <span className="h-9 w-9 border border-[#0A2540] flex items-center justify-center">
            <span className="font-serif text-lg text-[#0A2540] leading-none">
              M
            </span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-xl text-[#0A2540] tracking-tight">
              {BRAND.name}
            </span>
            <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-widest-plus text-[#94A3B8]">
              Est. Practice · Pathology · Cytology
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={testIdFor(l.label)}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative font-sans text-[11px] uppercase tracking-widest-plus transition-colors py-1 ${
                  isActive
                    ? "text-[#0A2540]"
                    : "text-[#475569] hover:text-[#0A2540]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-px transition-all ${
                      isActive ? "bg-[#B08A4A]" : "bg-transparent"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <a
            href={teleconsultUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="bg-[#0A2540] text-white hover:bg-[#061A2E] px-5 py-3 text-[10px] tracking-widest-plus uppercase transition-all duration-300 border border-[#0A2540] font-medium"
          >
            Book Teleconsultation
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-2 text-[#0A2540]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#E2E8F0] bg-white"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${testIdFor(l.label)}`}
                className={({ isActive }) =>
                  `font-sans text-sm uppercase tracking-widest-plus ${
                    isActive ? "text-[#0A2540]" : "text-[#475569]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={teleconsultUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="mt-2 inline-block bg-[#0A2540] text-white px-6 py-3 text-xs tracking-widest-plus uppercase text-center"
              onClick={() => setOpen(false)}
            >
              Book Teleconsultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
